import { SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Col, Input, message, Pagination, Popconfirm, Row, Space, Table, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as ArchiveRestore } from '../../Images/ArchiveRestore.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { fetchArchivedWorkOrderList, unArchiveWorkOrder } from '../../slices/work-order';
import dayjs from 'dayjs';

function ArchiveWorkOrderList() {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState('');
  const dispatch = useDispatch();
  const { data = [], totalRecord } = useSelector(state => state.workOrder);
  const handlePageChange = (newPage) => {
    setPage(newPage);
    setOrderBy('')
  };
  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo: (page - 1) * pagesize + index + 1,
  }));

  const handleRestore = (id) => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(unArchiveWorkOrder(id))
      .unwrap()
      .then(() => {
        message.success('Work Order Unarchived Successfully!');
        dispatch(
          fetchArchivedWorkOrderList({ page, pagesize, ...(search && { search: search }) }),
        );
      })
      .catch((err) => {
        console.error('Delete Error:', err);
        message.error(`Error: ${err.message || 'Failed to delete the profile.'}`);
      });
  };
  const columns = [
    { title: 'Sr. No.', dataIndex: 'SrNo', key: 'SrNo', sorter: true, width: 100, },

    {
      title: 'Order Date',
      dataIndex: 'order_date',
      key: 'order_date',
      sorter: true,
    },
    {
      title: 'Order No.',
      dataIndex: 'order_no',
      key: 'order_no',
      sorter: true,
    },
    {
      title: 'PO No.',
      dataIndex: 'purchase_order_no',
      key: 'purchase_order_no',
      sorter: true,
    },
    {
      title: 'Customer Name',
      dataIndex: 'bill_to',
      sorter: true,
      render: (bill_to) => {
        return (
          <span>
            {bill_to?.customer_name || 'N/A'}
          </span>
        )
      },
      key: 'customer_name',
    },
    {
      title: 'Total Weight',
      dataIndex: 'total_weight',
      key: 'total_weight',
      sorter: (a, b) => a.total_weight - b.total_weight,
    },
    {
      title: 'Packed Weight',
      dataIndex: 'packing_charge',
      key: 'packing_charge',
      sorter: true,
    },
    {
      title: 'Dispatched Weight',
      dataIndex: 'dispatched_weight',
      key: 'dispatched_weight',
      sorter: true,
    },
    {
      title: 'Pending Weight',
      dataIndex: 'pending_weight',
      key: 'pending_weight',
      sorter: true,
    },
    {
      title: 'Delivery Date',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
    },
    {
      title: 'Archive At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: true,
      render: (record) => {
        return (
          <span>{record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'} </span>
        )
      }
    },
    {
      title: 'Archive By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: true,
      render: (record) => {
        return (
          <span>{record ? `${record.first_name} ${record.last_name}` : 'N/A'} </span>
        )
      }

    },
    {
      title: 'Action',
      dataIndex: 'action',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip placement="top" title="Restore">
            <Popconfirm
              title="Are you sure you want to Unarchive this record?"
              onConfirm={() => handleRestore(record.id)} // Trigger delete on confirmation
              onCancel={() => message.info('Action cancelled')} // Optional cancel action
              okText="Yes"
              cancelText="No"
            >
              <ArchiveRestore className="ArchiverestorImg" />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: selectedKeys => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  useEffect(() => {
    dispatch(
      fetchArchivedWorkOrderList({
        page,
        pagesize,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
  }, [dispatch, page, pagesize, search, orderBy]);
  const debounceTimeout = useRef(null);
  const handleSearch = (e) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setSearch(e.target.value);
      setPage(1);
    }, 100); // 300ms delay
  };
  const resetFilter = () => {
    setSearch('');
    setOrderBy('');
    setPage(1);
  };
  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";

    if (sortField === "SrNo" && sortOrder === "ascend") {
      sortingEnumKey = "SrNo";
    }
    if (sortField === "SrNo" && sortOrder === "descend") {
      sortingEnumKey = "-SrNo";
    }
    if (sortField === "order_date" && sortOrder === "ascend") {
      sortingEnumKey = "order_date";
    }
    if (sortField === "order_date" && sortOrder === "descend") {
      sortingEnumKey = "-order_date";
    }
    if (sortField === "order_no" && sortOrder === "ascend") {
      sortingEnumKey = "order_no";
    }
    if (sortField === "order_no" && sortOrder === "descend") {
      sortingEnumKey = "-order_no";
    }
    if (sortField === "purchase_order_no" && sortOrder === "ascend") {
      sortingEnumKey = "purchase_order_no";
    }
    if (sortField === "purchase_order_no" && sortOrder === "descend") {
      sortingEnumKey = "-purchase_order_no";
    }
    if (sortField === "bill_to__customer_name" && sortOrder === "ascend") {
      sortingEnumKey = "bill_to__customer_name";
    }
    if (sortField === "bill_to__customer_name" && sortOrder === "descend") {
      sortingEnumKey = "-bill_to__customer_name";
    }
    if (sortField === "ship_to__customer_name" && sortOrder === "ascend") {
      sortingEnumKey = "ship_to__customer_name";
    }
    if (sortField === "ship_to__customer_name" && sortOrder === "descend") {
      sortingEnumKey = "-ship_to__customer_name";
    }
    if (sortField === "delivery_date" && sortOrder === "ascend") {
      sortingEnumKey = "delivery_date";
    }
    if (sortField === "delivery_date" && sortOrder === "descend") {
      sortingEnumKey = "-delivery_date";
    }
    if (sortField === "status" && sortOrder === "ascend") {
      sortingEnumKey = "status";
    }
    if (sortField === "status" && sortOrder === "descend") {
      sortingEnumKey = "-status";
    }

    if (sortField === "created_by" && sortOrder === "ascend") {
      sortingEnumKey = "created_by__first_name";
    }
    if (sortField === "created_by" && sortOrder === "descend") {
      sortingEnumKey = "-created_by__first_name";
    }
    if (sortField === "created_by__last_name" && sortOrder === "ascend") {
      sortingEnumKey = "created_by__last_name";
    }
    if (sortField === "created_by__last_name" && sortOrder === "descend") {
      sortingEnumKey = "-created_by__last_name";
    }
    if (sortField === "updated_by" && sortOrder === "ascend") {
      sortingEnumKey = "updated_by__first_name";
    }
    if (sortField === "updated_by" && sortOrder === "descend") {
      sortingEnumKey = "-updated_by__first_name";
    }
    if (sortField === "updated_by__last_name" && sortOrder === "ascend") {
      sortingEnumKey = "updated_by__last_name";
    }
    if (sortField === "updated_by__last_name" && sortOrder === "descend") {
      sortingEnumKey = "-updated_by__last_name";
    }
    return sortingEnumKey;
  };
  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    setPageSize(pagination.pageSize);
  };
  const handlePageSizeChange = (current, size) => {
    setPage(1); // Reset to first page if needed
    setPageSize(size); // You must have a setter or state update
  };

  return (
    <>
      <div className="mainwrap">
        <div className='sticyaction'>
          <Row className="pagenamerow" justify="space-between" align="middle">
            <Col>
              <h2>Archive Work Orders</h2>
              <div className="bredcrumbwrp">
                <Link to="/work-order-list" className="back">
                  <Back /> BACK
                </Link>
                <Breadcrumb
                  items={[
                    { title: <Link to="/dashboard">Home</Link> },
                    { title: <Link to="/work-order-list">Work Orders</Link> },
                    { title: 'Archive Work Orders' }
                  ]}
                />
              </div>
            </Col>
            <Col>
              <Space size="middle">
                <Input
                  placeholder="Search"
                  className='searchbox'
                  prefix={<SearchOutlined />}
                  value={search}
                  onChange={handleSearch}
                  style={{ width: '100%' }}
                />
                <Link >
                  <Tooltip title="Filter">
                    <Button type="primary" disabled={!search && !orderBy} className='iconlink' onClick={resetFilter}>
                      <FilterIcon />
                    </Button>
                  </Tooltip>
                </Link>
              </Space>
            </Col>

          </Row>
        </div>
        <div className="tablewrp">
          <Table
            rowSelection={rowSelection}
            dataSource={dataSource}
            columns={columns}
            pagination={false}
            onChange={handleTableChange}
          />
        </div>
        <Pagination
          current={page}
          pageSize={pagesize}
          total={totalRecord}
          showSizeChanger
          pageSizeOptions={['20', '50', '100']}
          onShowSizeChange={handlePageSizeChange}
          showTotal={total => `Total ${total} items`}
          onChange={handlePageChange}
          locale={{ items_per_page: 'Items / Page' }}
        />
      </div >
    </>
  );
}

export default ArchiveWorkOrderList;
